import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { PAYMENT_MODE_OPTIONS_PRODUCT_RECEIVING } from '../../common/constant/page-constant'
import { formatProductListLabel } from '../../common/utils/productUtils'
import SideDrawer from '../Common/SideDrawer/SideDrawer'
import currencyFormatter from './../../common/utils/currencyFormatterUtils'
import './ManageInventory.scss'
import PrintAndDownloadButton from './../../common/component/printAndDownloadButton'
import { formatInvoiceDate } from './../../common/utils/dateFormatter';


const formatInvoiceTime = (timeString) => {
  if (!timeString) return ''
  const str = String(timeString)

  const hmsAmPm = str.match(/(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)/i)
  if (hmsAmPm) {
    return `${parseInt(hmsAmPm[1], 10)}:${hmsAmPm[2]}:${hmsAmPm[3]} ${hmsAmPm[4].toUpperCase()}`
  }

  const hms = str.match(/(\d{1,2}):(\d{2}):(\d{2})/)
  if (hms) {
    let h = parseInt(hms[1], 10)
    const ampm = h >= 12 ? 'PM' : 'AM'
    h = h % 12 || 12
    return `${h}:${hms[2]}:${hms[3]} ${ampm}`
  }

  const date = new Date(str)
  if (!isNaN(date.getTime())) {
    let h = date.getHours()
    const m = String(date.getMinutes()).padStart(2, '0')
    const s = String(date.getSeconds()).padStart(2, '0')
    const ampm = h >= 12 ? 'PM' : 'AM'
    h = h % 12 || 12
    return `${h}:${m}:${s} ${ampm}`
  }

  return str
}

function ViewInventory({ open, handleDrawerToggle, invoiceData, onAction }) {
  const navigate = useNavigate()
  const hasVendor = !!invoiceData?.vendor_details
  const hasPharma = !!invoiceData?.pharma_details
  const hasMultiSupplier = invoiceData?.product_details?.some((p) => p.manufacturer_name && p.manufacturer_name !== invoiceData?.pharma_details?.name)
  const headerEntity = hasVendor
    ? {
        name: invoiceData.vendor_details.name,
        contact: invoiceData.vendor_details.contact,
      }
    : hasPharma && !hasMultiSupplier
    ? {
        name: invoiceData.pharma_details.name,
        contact: invoiceData.pharma_details.mobile_no,
      }
    : null

  const products = invoiceData?.product_details || []

  let totalQty = 0
  let totalBonus = 0
  let totalSubSum = 0
  products.forEach((p) => {
    const qty = parseFloat(p.quantity || 0)
    const bonus = parseFloat(p.bonus_quantity || 0)
    const cpu = parseFloat(p.cost_per_unit || 0)
    totalQty += qty
    totalBonus += bonus
    totalSubSum += qty * cpu
  })

  const paymentModeLabel = invoiceData?.payment_mode
    ? PAYMENT_MODE_OPTIONS_PRODUCT_RECEIVING.find(
        (item) => item.name === invoiceData.payment_mode
      )?.label || invoiceData.payment_mode
    : ''

  return (
    <div className="inventoryViewDrawer">
      <SideDrawer
        handleDrawerToggle={handleDrawerToggle}
        open={open}
        drawerHeading="Manage Inventory"
      >
        {invoiceData && (
          <div className="viewProductWrap productReceivingInvoice">
            <div
              className={`invoiceHeader ${headerEntity ? '' : 'noVendor'}`}
            >
              <div className="invoiceHeaderLeft">
                <div className="invoiceTitle">Product Receiving Invoice</div>
                <div className="metaLine">
                  <strong>Received by:</strong> {invoiceData.created_by || '-'}
                </div>
                <div className="metaLine">
                  <strong>Invoice:</strong> {invoiceData.invoice_number || '-'}
                </div>
              </div>

              {headerEntity && (
                <div className="invoiceHeaderCenter">
                  <div className="vendorName">{headerEntity.name}</div>
                  {headerEntity.contact && (
                    <div className="vendorContact">{headerEntity.contact}</div>
                  )}
                  {invoiceData.bill_type && (
                    <div className="vendorBillType">
                      {invoiceData.bill_type}
                    </div>
                  )}
                </div>
              )}

              <div className="invoiceHeaderRight">
                <div className="metaLine">
                  <strong>Date:</strong>{' '}
                  {formatInvoiceDate(invoiceData.date_of_bill)}
                </div>
                <div className="metaLine">
                  <strong>Time:</strong>{' '}
                  {formatInvoiceTime(
                    invoiceData.time_of_bill || invoiceData.created_at_iso
                  )}
                </div>
              </div>
            </div>

            <div className="productDescriptionWrap">
              <table className="invoiceTable productReceivingInvoiceTable">
                <thead>
                  <tr>
                    <th width="4%">SL No</th>
                    <th width="18%">Product Name (Size)-Strength</th>
                    <th width="9%">Category</th>
                    <th width="8%">Batch No</th>
                    <th width="9%">Expiry Date</th>
                    <th width="5%">Qty</th>
                    <th width="7%">Bonus Qty</th>
                    <th width="7%" className="tar">CPU</th>
                    <th width="7%" className="tar">RPU</th>
                    <th width="9%" className="tar">Sub total</th>
                    {(hasVendor || hasMultiSupplier) && (
                      <th width="17%">Suppliers/ Manufacturer</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, index) => {
                    const qty = parseFloat(product.quantity || 0)
                    const bonus = parseFloat(product.bonus_quantity || 0)
                    const cpu = parseFloat(product.cost_per_unit || 0)
                    const rpu = parseFloat(product.rate_per_unit || 0)
                    const sub = qty * cpu

                    const formattedName = formatProductListLabel(
                      {
                        name: product.product_name || '',
                        size: product.size,
                        dose: product.dose,
                        strength: product.strength,
                        category: product.category,
                      },
                      { includeCategory: false }
                    )

                    return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td className="tdWrap scondln">
                          {formattedName}
                          {product.is_approved ? '' : ' *'}
                        </td>
                        <td>{product.category || ''}</td>
                        <td>{product.batch_number || '-'}</td>
                        <td>
                          {product.expiry_date
                            ? formatInvoiceDate(product.expiry_date)
                            : '-'}
                        </td>
                        <td>{qty}</td>
                        <td>{bonus}</td>
                        <td className="tar">{cpu.toFixed(2)}</td>
                        <td className="tar">{rpu.toFixed(2)}</td>
                        <td className="tar">{sub.toFixed(2)}</td>
                        {(hasVendor || hasMultiSupplier) && (
                          <td>{product.manufacturer_name || ''}</td>
                        )}
                      </tr>
                    )
                  })}
                  <tr className="totalRow">
                    <td colSpan={5} className="tar">
                      <strong>Total</strong>
                    </td>
                    <td>
                      <strong>{totalQty}</strong>
                    </td>
                    <td>
                      <strong>{totalBonus}</strong>
                    </td>
                    <td colSpan={2}></td>
                    <td className="tar">
                      <strong>{totalSubSum.toFixed(2)}</strong>
                    </td>
                    {(hasVendor || hasMultiSupplier) && <td></td>}
                  </tr>
                </tbody>
              </table>

              <div className="invoiceBottomRow dFlex jcSpaceBetween">
                <div className="invoiceNotesBox">
                  <div className="notesLabel">
                    <strong>Notes:</strong>
                  </div>
                  <div className="notesValue">{invoiceData.notes || ''}</div>
                  <div className="paymentStatusRow">
                     <strong>Payment Status: </strong>
                    {invoiceData.payment_status === 'PENDING'
                      ? 'Pending'
                      : invoiceData.payment_status === 'PAID_VOUCHER' || invoiceData.payment_status === 'PAID_ONLINE'
                        ? 'Paid'
                        : invoiceData.status === 'RECEIVED' ? 'Paid' : 'Unpaid'}
                  </div>
                  
                  
                   {invoiceData.payment_mode && (
                    <div className="paymentModeRow">
                      <strong>Payment Date: </strong>                   
                      {invoiceData.updated_at && formatInvoiceDate(invoiceData.updated_at)}
                    </div>
                  )}

                  {invoiceData.payment_mode && (
                    <div className="paymentModeRow">
                      <strong>Payment Mode: </strong>
                      {paymentModeLabel}
                    </div>
                  )}

                  {products.some((p) => !p.is_approved) && (
                    <div className="approvalNote">
                      * marked products require approval from DG HealthCare
                    </div>
                  )}
                  {invoiceData.pharma_details &&
                    !invoiceData.pharma_details.is_approved && (
                      <div className="approvalNote">
                        Supplier approval pending from DG HealthCare
                      </div>
                    )}
                </div>

                <div className="invoiceTotalsBox">
                  <div className="totalLine strong">
                    <span>Amount (BDT):</span>
                    <span>
                      {currencyFormatter(
                        parseFloat(invoiceData.amount || 0).toFixed(2)
                      )}
                    </span>
                  </div>
                  <div className="totalLine">
                    <span>Total TP (BDT):</span>
                    <span>
                      {currencyFormatter(
                        parseFloat(invoiceData.total_tp || 0).toFixed(2)
                      )}
                    </span>
                  </div>
                  <div className="totalLine">
                    <span>Total VAT (BDT):</span>
                    <span>
                      {currencyFormatter(
                        parseFloat(invoiceData.vat || 0).toFixed(2)
                      )}
                    </span>
                  </div>
                  <div className="totalLine">
                    <span>
                      Trade Discount (
                      {invoiceData.trade_discount_percentage || 0}%):
                    </span>
                    <span>
                      {currencyFormatter(
                        parseFloat(
                          invoiceData.trade_discount_amount || 0
                        ).toFixed(2)
                      )}
                    </span>
                  </div>
                  <div className="totalLine grandTotal">
                    <span>
                      <strong>Total (BDT):</strong>
                    </span>
                    <span>
                      <strong>
                        {currencyFormatter(
                          parseFloat(invoiceData.total_amount || 0).toFixed(2)
                        )}
                      </strong>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <PrintAndDownloadButton
              onAction={onAction}
              invoiceData={invoiceData}
            />
          </div>
        )}
      </SideDrawer>
    </div>
  )
}

const mapStateToProps = (state) => {
  return {}
}

const mapDispatchToProps = (dispatch) => {
  return {}
}

ViewInventory.propTypes = {
  open: PropTypes.bool,
  handleDrawerToggle: PropTypes.func,
  invoiceData: PropTypes.object,
}

export default connect(mapStateToProps, mapDispatchToProps)(ViewInventory)
